/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import moment from 'moment';

import { InstallBaseData } from 'services/basesServices';
import CollapsibleSection from 'design-system/CollapsibleSection';
import GasLevelMeter from 'design-system/GasLevelMeter';
import WeekCalendar, { DayItem } from 'design-system/WeekCalendar';
import RouteStatusNote from 'design-system/RouteStatusNote';
import InfoBanner from 'design-system/InfoBanner';
import Button from 'design-system/Button';
import StepHeader from 'design-system/StepHeader';
import Select from 'design-system/Select';
import Typography from 'design-system/Typography';
import { spacing } from 'design-system/tokens';

import { useNewRefuel } from './hooks/useNewRefuel';
import { buildDayItems } from './adapters';
import DadosResponsavelSection from './DadosResponsavelSection';

export interface RefuelLocationFormProps {
  /** Base em foco (vinda de `selectedBase`). */
  base: InstallBaseData;
  /** Índice 0-based do local no loop. */
  index: number;
  /** Total de locais selecionados. */
  total: number;
  /** Rótulo do botão de avanço (ex.: "Próximo local" / "Ir para o resumo"). */
  advanceLabel: string;
  /** Avança (próximo local, resumo, ou volta ao resumo se editando). */
  onAdvance: () => void;
  /** Abre a ajuda "Onde verifico o nível do meu gás?" (modal no container). */
  onGasHelp?: () => void;
  /** Realça de vermelho as seções pendentes (após tentativa de avançar). */
  showErrors?: boolean;
}

const RefuelLocationForm = ({
  base,
  index,
  total,
  advanceLabel,
  onAdvance,
  onGasHelp,
  showErrors = false,
}: RefuelLocationFormProps): React.ReactElement => {
  const {
    refuelData,
    responsibleByBase,
    handlePercentage,
    handleRefuelDate,
    handleResponsible,
  } = useNewRefuel();

  const refuel = refuelData.find((r) => r.baseName === base.name);
  const gasValue = refuel ? Number(refuel.currentPercentage) || 0 : 0;
  const selectedDate = refuel?.refuelDate || '';
  const isFixedRoute = refuel?.isFixedRoute || false;

  // Abre o calendário já na semana/mês da data escolhida (ao editar um local que
  // já tinha data). Se nada foi preenchido ainda, abre na data atual.
  const [weekStart, setWeekStart] = useState(() =>
    (selectedDate ? moment(selectedDate, 'YYYY-MM-DD') : moment()).startOf(
      'isoWeek'
    )
  );
  // Mês exibido no seletor — separado do weekStart, pois a semana ISO pode
  // começar no mês anterior (ex.: jump p/ Maio mostra 27/abr–03/mai, mas o
  // seletor deve dizer "Maio").
  const [viewMonth, setViewMonth] = useState(() =>
    (selectedDate ? moment(selectedDate, 'YYYY-MM-DD') : moment()).month()
  );

  const days: DayItem[] = buildDayItems(base.baseTerritory, weekStart);

  // Subtítulo do InfoBanner: "Quinta, 31/05/2026".
  const forecastSubtitle = (() => {
    if (!selectedDate) return '';
    const d = moment(selectedDate, 'YYYY-MM-DD');
    const wd = d.format('dddd').split('-')[0];
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)}, ${d.format(
      'DD/MM/YYYY'
    )}`;
  })();

  const responsible = responsibleByBase[base.name];
  const initialResponsible = responsible?.name
    ? {
        name: responsible.name,
        phone: responsible.phone,
        email: responsible.email,
      }
    : null;

  // Realce vermelho só nas seções que ainda faltam, após tentar avançar.
  const gasError = showErrors && !base.percentageValidate;
  const dateError = showErrors && !base.dateValidate;
  const responsibleError =
    showErrors &&
    (!responsible ||
      !responsible.name ||
      !responsible.phone ||
      !responsible.email ||
      !responsible.details);

  const handleGasChange = (value: number): void => {
    handlePercentage(base.name, String(value));
  };

  const handleSelectDate = (day: DayItem): void => {
    handleRefuelDate(base.name, day.date, day.status === 'route');
  };

  const months = moment.months();
  const capitalize = (text: string): string =>
    text.charAt(0).toUpperCase() + text.slice(1);

  // Navega semanas mantendo o seletor de mês em sincronia (usa a quinta-feira
  // como mês "canônico" da semana ISO).
  const goToWeek = (week: moment.Moment): void => {
    setWeekStart(week);
    setViewMonth(week.clone().add(3, 'days').month());
  };

  // Seletor de mês: pula a faixa do WeekCalendar para o mês escolhido.
  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const newMonth = parseInt(e.target.value, 10);
    setViewMonth(newMonth);
    const now = moment();
    if (newMonth === now.month()) {
      setWeekStart(now.startOf('isoWeek'));
    } else {
      setWeekStart(
        moment().month(newMonth).startOf('month').startOf('isoWeek')
      );
    }
  };

  const handleResponsibleChange = (
    data: { name: string; phone: string; email: string } | null,
    comment: string
  ): void => {
    if (data) {
      handleResponsible(base.name, data.name, 'name');
      handleResponsible(base.name, data.phone, 'phone');
      handleResponsible(base.name, data.email, 'email');
    }
    handleResponsible(base.name, comment, 'details');
  };

  const gasHelpButton = onGasHelp ? (
    <Button variant="secondary" shape="pill" onClick={onGasHelp}>
      Onde verifico o nível do meu gás?
    </Button>
  ) : undefined;

  return (
    <div
      id="refuel-form-top"
      style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}
    >
      <StepHeader
        title={base.nickname}
        description="preencha os dados abaixo"
        state="active"
        counter={`Central de abastecimento ${index + 1} de ${total}`}
      />

      <CollapsibleSection
        title="Nível do gás"
        headerAction={gasHelpButton}
        defaultExpanded
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.xs,
            }}
          >
            <Typography as="p" size="md" weight="bold" color="#3D3D3D">
              Informe o nível do seu gás atual (%)
            </Typography>
            <Typography as="p" size="sm" weight="regular" color="#808495">
              Para evitar o risco de ficar sem gás, recomendamos que o
              abastecimento seja feito ao atingir 30% da capacidade do tanque.
            </Typography>
          </div>
          <GasLevelMeter
            value={gasValue}
            onChange={handleGasChange}
            label={base.nickname}
            error={gasError}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Data de entrega" defaultExpanded>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: spacing.lg,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xs,
              }}
            >
              <Typography as="p" size="md" weight="bold" color="#3D3D3D">
                Qual o melhor dia para o abastecimento?
              </Typography>
              <Typography as="p" size="sm" weight="regular" color="#808495">
                Selecione aqui a melhor data para realizarmos a entrega do seu
                gás.
              </Typography>
            </div>
            <Select
              shape="pill"
              size="xl"
              aria-label="Selecione o mês"
              value={viewMonth}
              onChange={handleMonthChange}
              style={{ minWidth: 168 }}
            >
              {months.map((m, i) => (
                <option key={m} value={i}>
                  {capitalize(m)}
                </option>
              ))}
            </Select>
          </div>

          <WeekCalendar
            days={days}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            error={dateError}
            onPrev={() => goToWeek(weekStart.clone().subtract(1, 'week'))}
            onNext={() => goToWeek(weekStart.clone().add(1, 'week'))}
          />
          {selectedDate && (
            <>
              <div style={{ borderTop: '1px dashed #D9D9D9' }} />
              <InfoBanner
                title="Previsão de abastecimento"
                subtitle={forecastSubtitle}
              >
                A data indicada corresponde à <strong>previsão</strong> do seu
                abastecimento. Caso haja necessidade de alteração, a Ultragaz
                realizará até as 16h do dia anterior. Recomendamos que{' '}
                <strong>confirme</strong> a programação próximo à data,
                acessando a <strong>tela inicial do Portal</strong>.
              </InfoBanner>
              <RouteStatusNote status={isFixedRoute ? 'route' : 'off-route'} />
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* DadosResponsavelSection já renderiza o próprio CollapsibleSection. */}
      <DadosResponsavelSection
        key={base.name}
        initialSelected={initialResponsible}
        initialComment={responsible?.details || ''}
        onChange={handleResponsibleChange}
        error={responsibleError}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          shape="pill"
          size="xl"
          onClick={onAdvance}
          style={{ minWidth: 240 }}
        >
          {advanceLabel}
        </Button>
      </div>
    </div>
  );
};

export default RefuelLocationForm;
